'use client';

import React, { FC, useState } from 'react';
import { Link } from '@/navigation';
import useAuth from '@/context/AuthContext';
import {
  TypeGlobalNavFields,
  TypeLink_externalFields,
  TypeLink_internal,
} from '@/core/generated-types';
import { slugBuilder } from '@/core/contentfulConfig';
import { CONTENT_TYPE_LINK } from '@/core/constants';
import { useUserMenuLinks } from '../../hooks/useUserMenuLinks';
import { useDebouncedHover } from '../../hooks/useDebouncedHover';
import { Translations } from '@/utils/translations/generateTranslationObject';
import { UserIcon } from '../../UserIcon/UserIcon';

export interface UserMenuProps {
  signInLink: TypeGlobalNavFields['signInLink'];
  linkMenuLoggedIn: TypeGlobalNavFields['linkMenuLoggedIn'];
  translations: Translations;
}

export const UserMenu: FC<UserMenuProps> = ({
  signInLink,
  linkMenuLoggedIn,
  translations,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { hardcodedLinks } = useUserMenuLinks(translations);
  const { user } = useAuth();
  const { handleMouseEnter, handleMouseLeave } = useDebouncedHover({
    onShow: () => setIsDropdownOpen(true),
    onHide: () => setIsDropdownOpen(false),
  });

  // Unauthenticated user
  if (!user) {
    const isExternalSignInLink =
      signInLink.sys.contentType.sys.id === CONTENT_TYPE_LINK.EXTERNAL;
    const signInHref = isExternalSignInLink
      ? (signInLink.fields as TypeLink_externalFields).linkUrl || '/'
      : slugBuilder(signInLink as TypeLink_internal);

    return (
      <Link href={signInHref}>
        <UserIcon />
      </Link>
    );
  }

  // Authenticated user
  return (
    <div
      className={`relative`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* User menu trigger */}
      <div className="flex items-center cursor-pointer gap-2 hover:text-blue-deep ">
        <UserIcon />
        <span className="text-base hover:underline font-bold">
          {translations.hello || 'Hello'}, {user.attributes?.name || 'User'}
        </span>
      </div>

      {/* Dropdown menu */}
      {isDropdownOpen && (
        <div className="absolute mt-[32px] right-0 text-sm top-full bg-white border border-gray-200 rounded-md shadow-lg z-50 p-4 flex flex-col gap-2">
          {/* Hardcoded links first */}
          {hardcodedLinks?.map((link) => (
            <Link
              key={link.id}
              href={link.href}
              onClick={link.onClick}
              className="block whitespace-nowrap hover:text-blue-deep hover:underline"
            >
              {link.label}
            </Link>
          ))}

          {/* Contentful links after hardcoded links */}
          {linkMenuLoggedIn?.map((link) => {
            const isExternalLink =
              link.sys.contentType.sys.id === CONTENT_TYPE_LINK.EXTERNAL;
            const href = isExternalLink
              ? (link.fields as TypeLink_externalFields).linkUrl || '#'
              : slugBuilder(link as TypeLink_internal);

            return (
              <Link
                key={link.sys.id}
                href={href}
                className="block whitespace-nowrap hover:text-blue-deep hover:underline"
              >
                {link.fields.linkText}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default React.memo(UserMenu);
